import { useQuery } from "@tanstack/react-query";
import { AlertCircle, AlertTriangle, CheckCircle, Info } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { trpc } from "@/utils/trpc";

function formatCurrency(amount: number) {
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(amount);
}

function getStatusBadge(status: string) {
  switch (status) {
    case "exceeded":
      return (
        <Badge variant="destructive" className="gap-1">
          <AlertCircle className="h-3 w-3" />
          Excedido
        </Badge>
      );
    case "near_max":
      return (
        <Badge variant="outline" className="gap-1 border-orange-500 text-orange-700">
          <AlertTriangle className="h-3 w-3" />
          Próximo do limite
        </Badge>
      );
    case "below_min":
      return (
        <Badge variant="outline" className="gap-1 border-blue-500 text-blue-700">
          <Info className="h-3 w-3" />
          Abaixo do mínimo
        </Badge>
      );
    case "ok":
      return (
        <Badge variant="outline" className="gap-1 border-green-500 text-green-700">
          <CheckCircle className="h-3 w-3" />
          OK
        </Badge>
      );
    default:
      return (
        <Badge variant="secondary" className="gap-1">
          Sem meta
        </Badge>
      );
  }
}

export function CategoryAnalysis() {
  // Fetch category health
  const categoryHealth = useQuery(trpc.categories.getHealth.queryOptions({}));

  // Fetch recommendations
  const recommendations = useQuery(
    trpc.categories.getRecommendations.queryOptions()
  );

  // Loading state
  if (categoryHealth.isLoading || recommendations.isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="text-muted-foreground">Carregando análise...</div>
      </div>
    );
  }

  // Error state
  if (categoryHealth.isError || recommendations.isError) {
    return (
      <Card className="border-red-200">
        <CardContent className="pt-6">
          <div className="flex items-center gap-2 text-red-600">
            <AlertCircle className="h-5 w-5" />
            <span>Erro ao carregar análise de categorias</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  const health = categoryHealth.data;
  const recs = recommendations.data;

  if (!health || !recs) {
    return null;
  }

  // Filter categories by status
  const categoriesWithIssues = health.categories.filter(
    (c) => c.status === "exceeded" || c.status === "near_max" || c.status === "below_min"
  );
  const categoriesOk = health.categories.filter((c) => c.status === "ok");
  const categoriesWithoutTarget = health.categories.filter(
    (c) => c.status === "no_target"
  );

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Total de Renda</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="font-bold text-2xl text-green-600">
              {formatCurrency(health.totalIncome)}
            </div>
            <p className="text-muted-foreground text-xs">Renda mensal</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Total Gasto</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="font-bold text-2xl">
              {formatCurrency(health.totalSpent)}
            </div>
            <p className="text-muted-foreground text-xs">
              {health.totalIncome > 0
                ? `${((health.totalSpent / health.totalIncome) * 100).toFixed(1)}% da renda`
                : ""}
            </p>
          </CardContent>
        </Card>

        <Card className={health.categoriesWithIssues > 0 ? "border-red-200" : "border-green-200"}>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Com Problemas</CardTitle>
          </CardHeader>
          <CardContent>
            <div
              className={`font-bold text-2xl ${health.categoriesWithIssues > 0 ? "text-red-600" : "text-green-600"}`}
            >
              {health.categoriesWithIssues}
            </div>
            <p className="text-muted-foreground text-xs">
              {health.categoriesWithIssues === 0 ? "✅ Tudo OK" : "⚠️ Requer atenção"}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Dentro dos Limites</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="font-bold text-2xl text-green-600">{health.categoriesOk}</div>
            <p className="text-muted-foreground text-xs">
              de {health.categories.length} categorias
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Recommendations */}
      {recs.recommendations.length > 0 && (
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Recomendações</AlertTitle>
          <AlertDescription>
            <ul className="mt-2 list-disc space-y-1 pl-4">
              {recs.recommendations.map((rec, index) => (
                <li key={index} className="text-sm">
                  {rec}
                </li>
              ))}
            </ul>
          </AlertDescription>
        </Alert>
      )}

      {/* Categories with Issues */}
      {categoriesWithIssues.length > 0 && (
        <Card className="border-red-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-red-600" />
              Categorias com Problemas ({categoriesWithIssues.length})
            </CardTitle>
            <CardDescription>
              Categorias que excederam ou estão próximas dos limites
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Categoria</TableHead>
                  <TableHead>Meta</TableHead>
                  <TableHead>Atual</TableHead>
                  <TableHead className="text-right">Gasto</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Progresso</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {categoriesWithIssues.map((cat) => {
                  const target = cat.percentageMax || cat.percentageMin || 0;
                  const barPercentage = Math.min((cat.percentageCurrent / target) * 100, 100);

                  return (
                    <TableRow key={cat.categoryId}>
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-2">
                          <span>{cat.categoryIcon}</span>
                          <span>{cat.categoryName}</span>
                          {cat.isBlocked && (
                            <Badge variant="secondary" className="text-xs">
                              Bloqueada
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        {cat.percentageMin && cat.percentageMax
                          ? `${cat.percentageMin}% - ${cat.percentageMax}%`
                          : cat.percentageMax
                            ? `≤ ${cat.percentageMax}%`
                            : cat.percentageMin
                              ? `≥ ${cat.percentageMin}%`
                              : "-"}
                      </TableCell>
                      <TableCell>
                        <span
                          className={
                            cat.status === "exceeded"
                              ? "font-semibold text-red-600"
                              : cat.status === "near_max"
                                ? "font-semibold text-orange-600"
                                : "font-semibold text-blue-600"
                          }
                        >
                          {cat.percentageCurrent.toFixed(1)}%
                        </span>
                      </TableCell>
                      <TableCell className="text-right font-mono">
                        {formatCurrency(cat.amountSpent)}
                      </TableCell>
                      <TableCell>{getStatusBadge(cat.status)}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Progress
                            value={barPercentage}
                            className="h-2 w-24"
                          />
                          <span className="text-muted-foreground text-xs">
                            {barPercentage.toFixed(0)}%
                          </span>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Categories OK */}
      {categoriesOk.length > 0 && (
        <Card className="border-green-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-green-600" />
              Categorias Saudáveis ({categoriesOk.length})
            </CardTitle>
            <CardDescription>Categorias dentro dos limites definidos</CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Categoria</TableHead>
                  <TableHead>Meta</TableHead>
                  <TableHead>Atual</TableHead>
                  <TableHead className="text-right">Gasto</TableHead>
                  <TableHead>Progresso</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {categoriesOk.map((cat) => {
                  const target = cat.percentageMax || 100;
                  const barPercentage = Math.min((cat.percentageCurrent / target) * 100, 100);

                  return (
                    <TableRow key={cat.categoryId}>
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-2">
                          <span>{cat.categoryIcon}</span>
                          <span>{cat.categoryName}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        {cat.percentageMin && cat.percentageMax
                          ? `${cat.percentageMin}% - ${cat.percentageMax}%`
                          : cat.percentageMax
                            ? `≤ ${cat.percentageMax}%`
                            : "-"}
                      </TableCell>
                      <TableCell>
                        <span className="font-semibold text-green-600">
                          {cat.percentageCurrent.toFixed(1)}%
                        </span>
                      </TableCell>
                      <TableCell className="text-right font-mono">
                        {formatCurrency(cat.amountSpent)}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Progress
                            value={barPercentage}
                            className="h-2 w-24"
                          />
                          <span className="text-muted-foreground text-xs">
                            {barPercentage.toFixed(0)}%
                          </span>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Categories Without Target */}
      {categoriesWithoutTarget.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Info className="h-5 w-5 text-muted-foreground" />
              Sem Metas Definidas ({categoriesWithoutTarget.length})
            </CardTitle>
            <CardDescription>
              Configure metas de % para melhor controle financeiro
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 md:grid-cols-2">
              {categoriesWithoutTarget.map((cat) => (
                <div
                  key={cat.categoryId}
                  className="flex items-center justify-between rounded-lg border p-3"
                >
                  <div className="flex items-center gap-2">
                    <span>{cat.categoryIcon}</span>
                    <div>
                      <p className="font-medium">{cat.categoryName}</p>
                      <p className="text-muted-foreground text-sm">
                        {formatCurrency(cat.amountSpent)} gastos este mês
                      </p>
                    </div>
                  </div>
                  <span className="font-semibold text-muted-foreground">
                    {cat.percentageCurrent.toFixed(1)}%
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
